import { AskableIconLoading } from '@askable/ui/core/askable-icon';
import { useKindeBrowserClient } from '@kinde-oss/kinde-auth-nextjs';
import { useState, useEffect } from 'react';

import { AUTH_CONFIG } from '@/src/network/auth';
import { isAnonymousParticipantToken } from '@/src/network/authExchange';
import { isInIframe } from '@/utils/isInIframe';
import { TypedStorage } from '@/utils/TypedStorage';

import type { ReactNode } from 'react';

type Props = {
  children: ReactNode;
};

/**
 * True when the parent application supplies a BYO anonymous participant token, in the URL or in the
 * `viewer_jwt` that the default `AUTH_CONFIG.getToken` reads. The Kinde token must not replace it: the
 * participant application embeds this application on an askable.com subdomain, so the browser sends
 * Kinde cookies for a different user, usually a client with no submission for the study.
 *
 * The iframe check keeps the Kinde session for a client in a full window, where an old `viewer_jwt` can
 * remain. The URL check covers the time before `AuthTokenProvider` stores the token and reloads.
 */
const hasEmbeddedAnonymousParticipantSession = (): boolean => {
  if (!isInIframe()) {
    return false;
  }
  const token = new URLSearchParams(window.location.search).get('userToken') ?? TypedStorage.get('viewer_jwt');
  return !!token && isAnonymousParticipantToken(token);
};

export const SetUpKinde = ({ children }: Props) => {
  const { getToken, isLoading, isAuthenticated, refreshData } = useKindeBrowserClient();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isAuthenticated && !hasEmbeddedAnonymousParticipantSession()) {
      AUTH_CONFIG.getToken = getToken;
      // Force Kinde to refresh the token then return the fresh value, so the
      // URQL `authExchange.refreshAuth` actually has a way to recover from an
      // expired token.
      AUTH_CONFIG.refreshToken = async () => {
        await refreshData();
        return getToken() ?? undefined;
      };
    }

    setReady(true);
  }, [isLoading]);

  if (!ready) {
    return <AskableIconLoading isFullScreen />;
  }

  return children;
};
